import { npcExpectedLabel } from '@/lib/npc-labels';
import { NPC_WORDING } from '@/lib/npc-wording';
import type {
  VersionedCounts,
  VersionedFilterChip,
  VersionedListSpec,
  VersionedSearch,
} from '@/lib/versioned-filters';
import {
  activeVersionedFilters,
  filterVersioned,
  hasActiveVersionedFilters,
  hasHiddenVersionedFilter,
  readVersionedFilters,
  resetVersionedFilters,
  versionedAuthors,
  versionedCountLabel,
  versionedCounts,
  versionedFiltersOf,
  versionedSearchOf,
} from '@/lib/versioned-filters';
import type { NpcBehaviour, NpcCard, NpcExpectedNoyau, NpcKind } from '@matter_extractor/shared';
import {
  NOYAU_CODES,
  NPC_BEHAVIOURS,
  NPC_BEHAVIOUR_LABELS,
  NPC_DEFAULT_SORT,
  NPC_KIND_LABELS,
  OUT_OF_BASE,
  npcBehaviourSchema,
  npcKindSchema,
  npcSortSchema,
  versionedFiltersShape,
} from '@matter_extractor/shared';
import { z } from 'zod';

/**
 * What the NPC library adds to the common rule set of §4 (npc.md §12, issue #210).
 *
 * Three dimensions of its own, and unlike the persona's they are not behind « Plus de
 * filtres »: §12 draws « Sorte », « Ce qu'il éprouve » and « Noyau attendu » in the row that is
 * always visible, in the place of the two referent selects (an NPC is cited by nothing).
 *
 *   - **sorte** - famous, written, real session;
 *   - **ce qu'il éprouve** - one of the eight behaviours of H5, the NPC carrying it among its
 *     labels;
 *   - **noyau attendu** - one of the six triplets, « hors base », or « sans noyau attendu » (H6).
 *
 * No sort of its own: the four common ones are all an NPC orders by.
 *
 * The filter schema lives HERE and not in `shared`: the contract of lot 0 carries the card and the
 * content, and what a page filters on is a matter of the page - the same argument that keeps the
 * Séries tab's columns out of the contract.
 */

export const NPC_FILTER_ALL = 'all';
/** The reserved value of « sans noyau attendu », beside the six triplets and « hors base ». */
export const NPC_EXPECTED_NONE = 'none';

export const npcExpectedFilterSchema = z.enum([
  NPC_FILTER_ALL,
  ...NOYAU_CODES,
  OUT_OF_BASE,
  NPC_EXPECTED_NONE,
]);
export type NpcExpectedFilter = z.infer<typeof npcExpectedFilterSchema>;

export const npcKindFilterSchema = z.union([z.literal(NPC_FILTER_ALL), npcKindSchema]);
export const npcBehaviourFilterSchema = z.union([z.literal(NPC_FILTER_ALL), npcBehaviourSchema]);

/** The options of the three selects, in the order they are listed. */
export const NPC_KIND_FILTER_OPTIONS = [NPC_FILTER_ALL, ...npcKindSchema.options] as const;
export const NPC_BEHAVIOUR_FILTER_OPTIONS = [NPC_FILTER_ALL, ...NPC_BEHAVIOURS] as const;

export const npcFiltersSchema = z
  .object({
    ...versionedFiltersShape,
    kind: npcKindFilterSchema.default(NPC_FILTER_ALL),
    behaviour: npcBehaviourFilterSchema.default(NPC_FILTER_ALL),
    expected: npcExpectedFilterSchema.default(NPC_FILTER_ALL),
    sort: npcSortSchema.default(NPC_DEFAULT_SORT),
  })
  .strict();
export type NpcFilters = z.infer<typeof npcFiltersSchema>;

/** The neutral state: every NPC, retired excluded, sorted by name. */
export const NPC_FILTERS_DEFAULT: NpcFilters = npcFiltersSchema.parse({});

/* ----------------------------------------------------------------- the words */

export const KIND_FILTER_LABEL: Readonly<Record<NpcKind | 'all', string>> = {
  all: 'Toutes',
  ...NPC_KIND_LABELS,
};

export const BEHAVIOUR_FILTER_LABEL: Readonly<Record<NpcBehaviour | 'all', string>> = {
  all: 'Tout',
  ...NPC_BEHAVIOUR_LABELS,
};

export const EXPECTED_FILTER_LABEL: Readonly<Record<NpcExpectedFilter, string>> = {
  all: 'Tous',
  ...Object.fromEntries(NOYAU_CODES.map((code) => [code, code])),
  [OUT_OF_BASE]: 'hors base',
  [NPC_EXPECTED_NONE]: 'sans noyau attendu',
} as Readonly<Record<NpcExpectedFilter, string>>;

/* ---------------------------------------------------------------- the matching */

function matchesExpected(expected: NpcExpectedNoyau | null, filter: NpcExpectedFilter): boolean {
  if (filter === NPC_FILTER_ALL) return true;
  if (filter === NPC_EXPECTED_NONE) return expected === null;
  return expected === filter;
}

/** The spec of the layer for an NPC's three own dimensions. */
export const NPC_LIST_SPEC: VersionedListSpec<NpcCard, NpcFilters> = {
  defaults: NPC_FILTERS_DEFAULT,

  read: (search) => {
    const patch: Partial<NpcFilters> = {};
    const kind = npcKindFilterSchema.safeParse(search.kind);
    if (kind.success && kind.data !== NPC_FILTER_ALL) patch.kind = kind.data;
    const behaviour = npcBehaviourFilterSchema.safeParse(search.behaviour);
    if (behaviour.success && behaviour.data !== NPC_FILTER_ALL) patch.behaviour = behaviour.data;
    const expected = npcExpectedFilterSchema.safeParse(search.expected);
    if (expected.success && expected.data !== NPC_FILTER_ALL) patch.expected = expected.data;
    return patch;
  },

  write: (filters) => {
    const search: Partial<NpcFilters> = {};
    if (filters.kind !== NPC_FILTER_ALL) search.kind = filters.kind;
    if (filters.behaviour !== NPC_FILTER_ALL) search.behaviour = filters.behaviour;
    if (filters.expected !== NPC_FILTER_ALL) search.expected = filters.expected;
    return search;
  },

  matches: (card, filters) =>
    (filters.kind === NPC_FILTER_ALL || card.kind === filters.kind) &&
    (filters.behaviour === NPC_FILTER_ALL || card.behaviours.includes(filters.behaviour)) &&
    matchesExpected(card.expectedNoyau, filters.expected),

  chips: (filters): VersionedFilterChip<NpcFilters>[] => {
    const chips: VersionedFilterChip<NpcFilters>[] = [];
    if (filters.kind !== NPC_FILTER_ALL) {
      chips.push({
        id: 'kind',
        label: `Sorte : ${KIND_FILTER_LABEL[filters.kind].toLowerCase()}`,
        patch: { kind: NPC_FILTER_ALL },
      });
    }
    if (filters.behaviour !== NPC_FILTER_ALL) {
      chips.push({
        id: 'behaviour',
        label: `Ce qu’il éprouve : ${NPC_BEHAVIOUR_LABELS[filters.behaviour].toLowerCase()}`,
        patch: { behaviour: NPC_FILTER_ALL },
      });
    }
    if (filters.expected !== NPC_FILTER_ALL) {
      chips.push({
        id: 'expected',
        label: `Noyau attendu : ${
          filters.expected === NPC_EXPECTED_NONE
            ? 'aucun'
            : npcExpectedLabel(filters.expected as NpcExpectedNoyau)
        }`,
        patch: { expected: NPC_FILTER_ALL },
      });
    }
    return chips;
  },

  sorts: {},
};

/* ------------------------------------------------ the layer, bound to §12 */

export type NpcSearch = VersionedSearch<NpcFilters>;
export type { VersionedCounts as NpcCounts };

export const readNpcFilters = (search: Record<string, unknown>): NpcSearch =>
  readVersionedFilters(search, NPC_LIST_SPEC);

export const npcFiltersOf = (search: NpcSearch): NpcFilters =>
  versionedFiltersOf(search, NPC_LIST_SPEC);

export const npcSearchOf = (filters: NpcFilters): NpcSearch =>
  versionedSearchOf(filters, NPC_LIST_SPEC);

export const filterNpcs = (cards: readonly NpcCard[], filters: NpcFilters): NpcCard[] =>
  filterVersioned(cards, filters, NPC_LIST_SPEC);

export const npcCounts = (cards: readonly NpcCard[], filters: NpcFilters): VersionedCounts =>
  versionedCounts(cards, filters, NPC_LIST_SPEC);

export const npcCountLabel = (counts: VersionedCounts): string =>
  versionedCountLabel(counts, NPC_WORDING.noun);

export const npcAuthors = versionedAuthors;

export const activeNpcFilters = (filters: NpcFilters): VersionedFilterChip<NpcFilters>[] =>
  activeVersionedFilters(filters, NPC_LIST_SPEC);

export const hasActiveNpcFilters = (filters: NpcFilters): boolean =>
  hasActiveVersionedFilters(filters, NPC_LIST_SPEC);

/**
 * Is a filter behind « Plus de filtres » armed? The three own dimensions are in the visible row,
 * so - unlike a persona's - they do not count: the spec the layer is asked with has no chips.
 */
export const hasHiddenNpcFilter = (filters: NpcFilters): boolean =>
  hasHiddenVersionedFilter(filters, { ...NPC_LIST_SPEC, chips: () => [] });

export const resetNpcFilters = (filters: NpcFilters): NpcFilters =>
  resetVersionedFilters(filters, NPC_LIST_SPEC);
